import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Droppable, DroppableProvided, DroppableStateSnapshot } from '@hello-pangea/dnd';
import { AlertCircle } from 'lucide-react';
import { List, useDynamicRowHeight, useListCallbackRef, RowComponentProps } from 'react-window';
import KanbanCard from './KanbanCard';
import { Negociacao, Profile } from '../../../types';
import { precisaAcao } from './kanbanWork';

interface KanbanColumnProps {
    id: string;
    label: string;
    negociacoes: Negociacao[];
    onOpenDetails: (negociacao: Negociacao) => void;
    onCreateVisita: (negociacao: Negociacao) => void;
    color?: string;
    profiles: Profile[];
    isLeanView?: boolean;
    isAdmin?: boolean;
    onChanged?: () => void;
    /** IDs de negociação com aviso de "lead atribuído" ainda não visto — liga o pulso do card. */
    naoAbertoIds?: Set<number>;
}

/** md do Tailwind (768px): abaixo disso a coluna não tem altura própria (h-auto — a
 *  página inteira rola, colunas empilhadas) e não há "viewport" fixo pro react-window
 *  medir. Virtualizar só faz sentido com o scroll interno da coluna, que só existe
 *  a partir do md (ver a classe `md:h-full` no container). */
const useColunaComScrollProprio = (): boolean => {
    const [desktop, setDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches);
    useEffect(() => {
        const mq = window.matchMedia('(min-width: 768px)');
        const onChange = () => setDesktop(mq.matches);
        onChange();
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return desktop;
};

interface RowProps {
    negociacoes: Negociacao[];
    onOpenDetails: (negociacao: Negociacao) => void;
    onCreateVisita: (negociacao: Negociacao) => void;
    profiles: Profile[];
    isLeanView?: boolean;
    isAdmin?: boolean;
    onChanged?: () => void;
    naoAbertoIds?: Set<number>;
}

// react-window mede a altura de cada linha pelo elemento raiz que este componente
// devolve (ele injeta `style` com a posição absoluta calculada) — por isso o card fica
// dentro de UMA div própria em vez de renderizar KanbanCard direto: o Draggable interno
// do KanbanCard não pode ser o nó medido (sua própria margem/posição muda durante o drag).
const Row = ({
    index, style, negociacoes, onOpenDetails, onCreateVisita, profiles, isLeanView, isAdmin, onChanged, naoAbertoIds,
}: RowComponentProps<RowProps>): React.ReactElement | null => {
    const negociacao = negociacoes[index];
    if (!negociacao) return null;
    return (
        <div style={style}>
            <KanbanCard
                negociacao={negociacao}
                index={index}
                onOpenDetails={onOpenDetails}
                onCreateVisita={onCreateVisita}
                profiles={profiles}
                isLeanView={isLeanView}
                isAdmin={isAdmin}
                onChanged={onChanged}
                naoAberto={naoAbertoIds?.has(negociacao.id)}
            />
        </div>
    );
};

interface ColumnBodyProps extends RowProps {
    provided: DroppableProvided;
    snapshot: DroppableStateSnapshot;
    virtualizar: boolean;
}

// Componente à parte só para poder chamar hooks (useDynamicRowHeight, useEffect...)
// de forma incondicional — o render-prop do <Droppable> não pode chamar hooks direto.
const ColumnBody: React.FC<ColumnBodyProps> = ({
    negociacoes, provided, snapshot, virtualizar,
    onOpenDetails, onCreateVisita, profiles, isLeanView, isAdmin, onChanged, naoAbertoIds,
}) => {
    // Estimativa inicial de altura por card — a lean view é bem mais baixa que a
    // completa. `key` reseta as medições guardadas quando o modo muda (as alturas
    // reais mudam por completo, não faz sentido reaproveitar as antigas).
    const rowHeight = useDynamicRowHeight({
        defaultRowHeight: isLeanView ? 76 : 230,
        key: isLeanView ? 'lean' : 'full',
    });

    const rowProps = useMemo<RowProps>(
        () => ({ negociacoes, onOpenDetails, onCreateVisita, profiles, isLeanView, isAdmin, onChanged, naoAbertoIds }),
        [negociacoes, onOpenDetails, onCreateVisita, profiles, isLeanView, isAdmin, onChanged, naoAbertoIds],
    );
    const rowKey = useCallback((index: number, data: RowProps) => data.negociacoes[index]?.id ?? index, []);

    // react-window não expõe uma prop pra receber um ref externo no elemento raiz (o
    // dele já é interno via listRef), então a ponte com o Droppable é manual: listRef
    // devolve o DOM node real (.element) e ele vira o innerRef do dnd. Só roda quando
    // está virtualizando — no modo não-virtualizado o ref vai direto na div (abaixo),
    // e chamar isso aqui por cima apagaria esse ref (listApi fica null sem <List> montado).
    const [listApi, listApiRef] = useListCallbackRef();
    useEffect(() => {
        if (!virtualizar) return;
        provided.innerRef(listApi?.element ?? null);
    }, [virtualizar, listApi, provided.innerRef]);

    const className = `flex-1 rounded-xl p-2 transition-colors overflow-y-auto custom-scrollbar border ${snapshot.isDraggingOver ? 'bg-primary/5 border-primary/20 dark:bg-primary/10' : 'bg-gray-100/50 dark:bg-gray-900/50 border-transparent hover:border-gray-200 dark:hover:border-gray-700'
        }`;

    if (virtualizar) {
        return (
            <List
                listRef={listApiRef}
                className={className}
                style={{ minHeight: '150px' }}
                rowComponent={Row}
                rowCount={negociacoes.length}
                rowHeight={rowHeight}
                rowProps={rowProps}
                rowKey={rowKey}
                overscanCount={8}
                {...provided.droppableProps}
            >
                {provided.placeholder}
            </List>
        );
    }

    return (
        <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={className}
            style={{ minHeight: '150px' }}
        >
            {negociacoes.map((negociacao, index) => (
                <KanbanCard key={negociacao.id} negociacao={negociacao} index={index} onOpenDetails={onOpenDetails} onCreateVisita={onCreateVisita} profiles={profiles} isLeanView={isLeanView} isAdmin={isAdmin} onChanged={onChanged} naoAberto={naoAbertoIds?.has(negociacao.id)} />
            ))}
            {provided.placeholder}

            {negociacoes.length === 0 && !snapshot.isDraggingOver && (
                <div className="h-32 flex flex-col items-center justify-center text-gray-300 dark:text-gray-600 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg m-2">
                    <span className="text-xs font-medium">Vazio</span>
                </div>
            )}
        </div>
    );
};

const KanbanColumn: React.FC<KanbanColumnProps> = React.memo(({ id, label, negociacoes, onOpenDetails, onCreateVisita, color, profiles, isLeanView, isAdmin, onChanged, naoAbertoIds }) => {
    const totalValue = negociacoes.reduce((acc, curr) => acc + (curr.consumo_medio_kwh || 0), 0);
    const acaoCount = negociacoes.reduce((acc, curr) => acc + (precisaAcao(curr) ? 1 : 0), 0);
    const virtualizar = useColunaComScrollProprio() && negociacoes.length > 0;

    return (
        <div className="w-full md:w-80 md:shrink-0 flex flex-col h-auto md:h-full md:max-h-full">
            {/* Header */}
            <div className="flex flex-col gap-1 mb-3 px-1">
                <div className="flex items-center justify-between">
                    <h3 className="font-poppins font-bold text-gray-700 dark:text-gray-200 text-sm uppercase tracking-wide">
                        {label}
                    </h3>
                    <span className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-300 text-xs font-bold px-2 py-0.5 rounded-full shadow-sm">
                        {negociacoes.length}
                    </span>
                </div>
                <div className="flex items-center gap-2 text-[11px] font-medium text-gray-400 dark:text-gray-500">
                    <span className="flex items-center gap-1"><span>Total:</span><span className="text-gray-600 dark:text-gray-300">{totalValue.toLocaleString('pt-BR')} kWh</span></span>
                    {acaoCount > 0 && (
                        <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 font-semibold" title="Leads que exigem ação (esfriando, termo pendente ou pendência documental)">
                            <AlertCircle size={11} /> {acaoCount} precisa{acaoCount > 1 ? 'm' : ''} de ação
                        </span>
                    )}
                </div>
                <div className="h-1 w-full rounded-full mt-1" style={{ backgroundColor: color || '#ccc' }} />
            </div>

            {/* Droppable Area */}
            <Droppable droppableId={id}>
                {(provided, snapshot) => (
                    <ColumnBody
                        provided={provided}
                        snapshot={snapshot}
                        virtualizar={virtualizar}
                        negociacoes={negociacoes}
                        onOpenDetails={onOpenDetails}
                        onCreateVisita={onCreateVisita}
                        profiles={profiles}
                        isLeanView={isLeanView}
                        isAdmin={isAdmin}
                        onChanged={onChanged}
                        naoAbertoIds={naoAbertoIds}
                    />
                )}
            </Droppable>
        </div>
    );
});

export default KanbanColumn;
